/*
 * Sign in.
 *
 * Two routes in: a code emailed to you (the default, since a partner Ops has
 * just created has no password yet), or a password. A code sign-in with
 * must_set_password asks for a password next; it can be skipped.
 *
 * Only partner accounts Wealthoak approved can sign in. There is no sign-up:
 * "Apply to become a partner" (/apply) sends an application the Wealthoak
 * team reviews, and approval emails the sign-in invite.
 */

import { useEffect, useRef, useState } from "react";
import { auth, errorMessage, type LoginOut } from "../api/client";
import { useAuth } from "../auth/AuthContext";
import BrandMark from "../ui/BrandMark";
import { APPLY_PATH, go } from "./route";
import "./Login.css";

type Method = "code" | "password";
type Step = "credentials" | "code_sent" | "set_password";

const RESEND_SECONDS = 30;
const MIN_PASSWORD = 10;
const PARTNERS_EMAIL = "info@wealthoak.ai";

export default function Login() {
  const { onSignedIn, endedMessage } = useAuth();

  const [method, setMethod] = useState<Method>("code");
  const [step, setStep] = useState<Step>("credentials");

  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [code, setCode] = useState("");
  const [newPassword, setNewPassword] = useState("");

  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [cooldown, setCooldown] = useState(0);

  const codeRef = useRef<HTMLInputElement>(null);
  const newPwRef = useRef<HTMLInputElement>(null);

  // Stops people hammering the button while an email is in flight. The server
  // rate-limits anyway, silently, so without this the UI would look like it
  // was working when nothing was being sent.
  useEffect(() => {
    if (cooldown <= 0) return;
    const t = setTimeout(() => setCooldown((c) => c - 1), 1000);
    return () => clearTimeout(t);
  }, [cooldown]);

  useEffect(() => {
    if (step === "code_sent") codeRef.current?.focus();
    if (step === "set_password") newPwRef.current?.focus();
  }, [step]);

  function fail(err: unknown) {
    setError(errorMessage(err));
  }

  async function settle(result: LoginOut) {
    if (result.must_set_password) {
      setStep("set_password");
      return;
    }
    await onSignedIn();
  }

  async function submitPassword(e: React.FormEvent) {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      await settle(await auth.loginWithPassword(email.trim(), password));
    } catch (err) {
      fail(err);
    } finally {
      setBusy(false);
    }
  }

  async function sendCode(e?: React.FormEvent) {
    e?.preventDefault();
    setBusy(true);
    setError(null);
    try {
      await auth.requestCode(email.trim());
      // The server says the same thing whether or not the account exists, so
      // this message must not imply it does.
      setNotice(`If ${email.trim()} has a partner account, a code is on its way.`);
      setStep("code_sent");
      setCooldown(RESEND_SECONDS);
    } catch (err) {
      fail(err);
    } finally {
      setBusy(false);
    }
  }

  async function submitCode(e: React.FormEvent) {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      await settle(await auth.verifyCode(email.trim(), code.trim()));
    } catch (err) {
      fail(err);
    } finally {
      setBusy(false);
    }
  }

  async function submitNewPassword(e: React.FormEvent) {
    e.preventDefault();
    if (newPassword.length < MIN_PASSWORD) return;
    setBusy(true);
    setError(null);
    try {
      await auth.setPassword(newPassword);
      await onSignedIn();
    } catch (err) {
      fail(err);
    } finally {
      setBusy(false);
    }
  }

  function switchMethod(next: Method) {
    setMethod(next);
    setError(null);
    setNotice(null);
  }

  const brand = (mobile: boolean) => (
    <div className={`auth__brand${mobile ? " auth__brand--mobile" : ""}`}>
      <BrandMark size={34} />
      <span className="auth__wordmark">Wealthoak</span>
      <span className="auth__tag">Partner</span>
    </div>
  );

  return (
    <div className="auth">
      <aside className="auth__aside">
        {brand(false)}
        <div>
          <h1 className="auth__headline">Refer clients.</h1>
          <p className="auth__sub">
            Submit referrals, follow the clients you brought to Wealthoak, and
            withdraw your commission to your bank, all in one place.
          </p>
        </div>
        <p className="auth__footnote">
          Wealthoak Advisory LLP · Pune, Maharashtra
          <br />
          Partner questions: <a href={`mailto:${PARTNERS_EMAIL}`}>{PARTNERS_EMAIL}</a>
        </p>
      </aside>

      <main className="auth__panel">
        <div className="auth__card">
          {brand(true)}

          {step === "set_password" ? (
            /* ---- set a password (first sign-in by code) ---- */
            <form onSubmit={submitNewPassword} noValidate>
              <h2 className="auth__title">Choose a password</h2>
              <p className="auth__hint">
                You&apos;re signed in. Set a password so you can get back in
                without waiting for a code.
              </p>
              <div className="auth__field" style={{ marginTop: 22 }}>
                <label className="auth__label" htmlFor="newpw">New password</label>
                <input
                  id="newpw"
                  ref={newPwRef}
                  className="auth__input"
                  type="password"
                  autoComplete="new-password"
                  minLength={MIN_PASSWORD}
                  required
                  value={newPassword}
                  aria-describedby="newpw-hint"
                  aria-invalid={Boolean(error)}
                  onChange={(e) => setNewPassword(e.target.value)}
                  placeholder={`At least ${MIN_PASSWORD} characters`}
                />
                <p id="newpw-hint" className="auth__field-hint">
                  {newPassword.length > 0 && newPassword.length < MIN_PASSWORD
                    ? `${MIN_PASSWORD - newPassword.length} more ${MIN_PASSWORD - newPassword.length === 1 ? "character" : "characters"} to go.`
                    : `At least ${MIN_PASSWORD} characters.`}
                </p>
              </div>
              <button className="auth__submit" disabled={busy || newPassword.length < MIN_PASSWORD}>
                {busy ? "Saving…" : "Save and continue"}
              </button>
              <div className="auth__row">
                <button type="button" className="auth__link" disabled={busy} onClick={() => void onSignedIn()}>
                  Skip for now
                </button>
              </div>
            </form>
          ) : step === "code_sent" ? (
            /* ---- enter the code ---- */
            <form onSubmit={submitCode} noValidate>
              <h2 className="auth__title">Enter your code</h2>
              <p className="auth__hint">
                We sent a 6-digit code to <strong>{email.trim()}</strong>. It
                expires in 10 minutes.
              </p>
              <div className="auth__field" style={{ marginTop: 22 }}>
                <label className="auth__label" htmlFor="code">Code</label>
                <input
                  id="code"
                  ref={codeRef}
                  className="auth__input auth__code"
                  inputMode="numeric"
                  autoComplete="one-time-code"
                  pattern="[0-9]*"
                  maxLength={6}
                  required
                  value={code}
                  aria-invalid={Boolean(error)}
                  onChange={(e) => setCode(e.target.value.replace(/\D/g, ""))}
                  placeholder="······"
                />
              </div>
              <button className="auth__submit" disabled={busy || code.length < 6}>
                {busy ? "Checking…" : "Sign in"}
              </button>
              <div className="auth__row">
                <button
                  type="button"
                  className="auth__link"
                  disabled={cooldown > 0 || busy}
                  onClick={() => void sendCode()}
                >
                  {cooldown > 0 ? `Resend in ${cooldown}s` : "Send a new code"}
                </button>
                <button
                  type="button"
                  className="auth__link"
                  onClick={() => {
                    setStep("credentials");
                    setCode("");
                    setError(null);
                    setNotice(null);
                  }}
                >
                  Use a different email
                </button>
              </div>
            </form>
          ) : (
            /* ---- email (+ password) ---- */
            <form onSubmit={method === "password" ? submitPassword : sendCode} noValidate>
              <h2 className="auth__title">Partner sign in</h2>
              <p className="auth__hint">
                {method === "password"
                  ? "Sign in with the password you set for your partner account."
                  : "We'll email you a 6-digit code. No password needed."}
              </p>

              <div className="auth__field">
                <label className="auth__label" htmlFor="email">Email</label>
                <input
                  id="email"
                  className="auth__input"
                  type="email"
                  autoComplete="username"
                  required
                  value={email}
                  aria-invalid={Boolean(error)}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="you@example.com"
                />
              </div>

              {method === "password" && (
                <div className="auth__field">
                  <label className="auth__label" htmlFor="password">Password</label>
                  <input
                    id="password"
                    className="auth__input"
                    type="password"
                    autoComplete="current-password"
                    required
                    value={password}
                    aria-invalid={Boolean(error)}
                    onChange={(e) => setPassword(e.target.value)}
                    placeholder="••••••••••"
                  />
                </div>
              )}

              <button
                className="auth__submit"
                disabled={busy || !email.trim() || (method === "password" && !password)}
              >
                {busy
                  ? method === "password" ? "Signing in…" : "Sending…"
                  : method === "password" ? "Sign in" : "Email me a code"}
              </button>

              <div className="auth__row">
                {method === "code" ? (
                  <button type="button" className="auth__link" onClick={() => switchMethod("password")}>
                    Use password instead
                  </button>
                ) : (
                  <button type="button" className="auth__link" onClick={() => switchMethod("code")}>
                    Forgot your password? Sign in with a code
                  </button>
                )}
              </div>
            </form>
          )}

          {error && <div className="auth__error" role="alert">{error}</div>}
          {!error && endedMessage && step === "credentials" && (
            <div className="auth__error" role="alert">
              {endedMessage} If you think this is a mistake, write to{" "}
              <a href={`mailto:${PARTNERS_EMAIL}`}>{PARTNERS_EMAIL}</a>.
            </div>
          )}
          {!error && notice && step === "code_sent" && (
            <div className="auth__notice" role="status">{notice}</div>
          )}

          <p className="auth__legal">
            Not a partner yet?{" "}
            <a href={APPLY_PATH} onClick={(e) => { e.preventDefault(); go(APPLY_PATH); }}>
              Apply to become a partner
            </a>
          </p>
        </div>
      </main>
    </div>
  );
}
